<script setup lang="ts">
import { DESIGN_UPSELL_TEMPLATE, FREE_CLEAN_TEMPLATE, isFreeCleanTemplate, PDF_TEMPLATES, type PdfTemplateId } from '~/constants/cvTemplates'
import type { JobPackLead } from '~/constants/pricing'
import type { CVData, CVTemplate } from '~/types'
import { guestCvSig } from '~/composables/useTailorEntry'
import type { TailorPreview, TailorSummary } from '~/utils/cvTailor'

/**
 * "Your CV is ready — now what?"
 *
 * Every way a finished CV can leave the site, as a path rather than a pile of
 * cards: first the result itself (the download, free in Basic, and keeping it
 * to edit), then the numbered steps that turn a general CV into interviews,
 * each tried for free before it costs anything (tailoring it to a posting,
 * the interview prep), then the Job Search Pack, which is all of them for
 * every posting, stacked from our own prices. The single clean PDF of a
 * designed layout closes it. The ad landing page and the homepage generator
 * both end here, so a visitor sees the same offer wherever the CV was made,
 * and the checkout plumbing lives in `useCvOffers`.
 */
const props = withDefaults(defineProps<{
  cvData: CVData
  template?: CVTemplate
  /** Shows up in /registration as `?from=…` when the visitor signs up from here. */
  signupSource?: string
  /** `guest_cv_generations` id from /api/ai/import-cv; links downloads to the CV in admin. */
  generationId?: string | null
  /**
   * Offer the free PDF: clean in Basic, watermarked in the rest. On in every
   * generator today; off hides it for a surface that only sells the CV.
   */
  freeDownload?: boolean
  /** Which builder made the CV; stored with a marketing consent given on the e-mail card. */
  consentSource?: 'cv-chat' | 'cv-paste'
  /** The account CV this one already is (the app's chat builder); saving updates it. */
  cvId?: string | null
  /**
   * Offer to tailor the CV to a job posting (LandingTailorDemo), on the page
   * and right after the free download, and sell the pack on it. On in the chat
   * builder.
   */
  jobMatch?: boolean
  /** Added to the journey events from here, e.g. an experiment and its variant. */
  trackingData?: Record<string, unknown>
  /**
   * The posting asked for while the CV was built (useCvChat, the paste
   * generator's dialog). The CV is not tailored to it: the tailoring card
   * waits with it filled in and says what tailoring to it costs, and for a
   * guest it is kept for the day-0 e-mail, which offers the same.
   */
  initialPosting?: string | null
  /**
   * A posting to tailor the CV to as soon as it is on screen, without the
   * card's button: the tailoring page asked for the CV and the posting first,
   * and its button was the click. Needs `jobMatch`.
   */
  tailorPosting?: string | null
  /**
   * How the column is laid out (the chat's result experiment, useChatResultExperiment):
   * `classic` the CV kept to edit and the offers under it (every other surface),
   * `path` the download first and the offers as numbered steps,
   * `staged` one step at a time on a panel, with a bar at the bottom on phones.
   */
  layout?: 'classic' | 'path' | 'staged'
}>(), {
  template: 'classic',
  signupSource: 'cv-generator',
  generationId: null,
  freeDownload: true,
  consentSource: 'cv-paste',
  cvId: null,
  jobMatch: false,
  trackingData: undefined,
  initialPosting: null,
  tailorPosting: null,
  layout: 'classic',
})

const emit = defineEmits<{
  /** The tailoring started or finished. */
  tailor: [summary: TailorSummary]
  /** Switch the preview (and what is downloaded or bought) to this layout. */
  pickTemplate: [template: PdfTemplateId]
}>()

const { t } = useI18n()
/** The pack less what was paid for this CV before (a tailored PDF, an interview prep). */
const packCredit = usePackCredit()

/**
 * The posting the visitor wants, and their CV rewritten for it (useTailorDemo):
 * one run shared by the card below and the panel after the free download.
 */
const tailorDemo = reactive(useTailorDemo({
  cv: () => props.cvData,
  template: () => props.template,
  generationId: () => props.generationId,
  trackingData: () => props.trackingData,
}))

watch(() => [tailorDemo.isRunning, tailorDemo.preview, tailorDemo.potentialScore] as const, ([running, preview]) => {
  emit('tailor', {
    running,
    matchScore: preview?.matchScore ?? null,
    matchScoreAfter: preview ? tailorDemo.potentialScore : null,
    changes: preview ? preview.locked.length + (preview.sample ? 1 : 0) : 0,
  })
})

/*
 * The posting from the build: into the tailoring card, not into the CV. The
 * visitor decides whether to have the CV tailored to it (the card's button);
 * nothing runs on its own. The posting may come after the CV is on screen
 * (the build dialog was still open), so it is watched, not read once.
 */
onMounted(() => watch(() => [props.initialPosting, props.generationId] as const, offerPosting, { immediate: true }))

// The credit changes once a tailored PDF or interview prep for this CV is paid.
onMounted(() => watch(() => [props.generationId, guestPaid.value] as const, ([id]) => packCredit.load(id), { immediate: true }))

/**
 * The posting they gave us (the build, the day-0 e-mail's link), while the
 * card still shows it untailored: the card then says what tailoring to it costs.
 */
const givenPosting = ref<string | null>(null)
const offersGivenPosting = computed(() => !!givenPosting.value && !tailorDemo.preview && !tailorDemo.isRunning
  // A link is replaced by the ad's text once it is read.
  && (tailorDemo.posting.trim() === givenPosting.value || tailorDemo.postingLoadedFrom === givenPosting.value))

/** Postings already kept for the day-0 e-mail, by CV. */
const keptPostings = new Set<string>()

function offerPosting() {
  const posting = props.initialPosting?.trim()
  if (!props.jobMatch || !posting) return
  givePosting(posting)
  keepPosting(posting)
}

function givePosting(posting: string) {
  if (tailorDemo.isRunning || tailorDemo.preview || (tailorDemo.posting.trim() && tailorDemo.posting.trim() !== givenPosting.value)) return
  givenPosting.value = posting
  tailorDemo.setPosting(posting)
}

/** The tailoring page's posting: run at once, and kept for the day-0 e-mail like a given one. */
function tailorNow() {
  const posting = props.tailorPosting?.trim()
  if (!props.jobMatch || !posting || tailorDemo.isRunning) return
  tailorDemo.setPosting(posting)
  keepPosting(posting)
  tailorDemo.run('card')
}
onMounted(() => watch(() => props.tailorPosting, tailorNow, { immediate: true }))

/** A guest's posting goes with the CV, so the day-0 e-mail can offer the tailoring too. */
function keepPosting(posting: string) {
  const id = props.generationId
  const sig = guestCvSig(id)
  if (!id || !sig || keptPostings.has(`${id}:${posting}`)) return
  keptPostings.add(`${id}:${posting}`)
  $fetch(`/api/guest-cv/${encodeURIComponent(id)}/posting`, { method: 'POST', body: { sig, posting } })
    .catch(() => keptPostings.delete(`${id}:${posting}`))
}

/**
 * The pack's headline, about this visitor instead of everybody: the score
 * their CV goes from and to once tailored to the posting they want, else the
 * role the CV is for, else the general line.
 */
/**
 * A low match does not make a headline: "from 15 % to 19 %" sells nothing and
 * oversells nothing, so below LOW_MATCH the role's headline is used instead.
 */
const tailored = computed(() => (tailorDemo.preview?.limited && !tailorDemo.lowMatch ? tailorDemo.preview : null))
/** The CV's headline is the role the chat asked them about; one short line, or nothing. */
const role = computed(() => {
  const title = props.cvData.title?.trim()
  return title && title.length <= 60 ? title : null
})
const packLead = computed<JobPackLead | undefined>(() => (tailored.value ? 'ats' : undefined))
const packHeadline = computed(() => {
  if (tailored.value) return t('landing.builder.pack.matchHeadline', { before: tailored.value.matchScore, after: tailorDemo.potentialScore })
  if (role.value) return t('landing.builder.pack.roleHeadline', { role: role.value })
  return undefined
})
const packSub = computed(() => {
  if (tailored.value) return t('landing.builder.pack.matchSub')
  if (role.value) return t('landing.builder.pack.roleSub')
  return undefined
})

/** Basic downloads clean for everyone, so the single clean PDF has nothing to sell. */
const isBasic = computed(() => isFreeCleanTemplate(props.template))

const {
  error,
  isExporting,
  isStartingCheckout,
  isClaiming,
  isScriptLoading,
  checkoutUrl,
  checkoutPlanId,
  showSignup,
  signupOfferLabel,
  confirmEmailSentTo,
  guestPaid,
  guestToken,
  guestDownloadUrl,
  guestClaimUrl,
  packAwaitingAccount,
  canBuyPack,
  canBuySingle,
  canBuyTailored,
  canBuyKit,
  guestPlan,
  isPremium,
  isAuthenticated,
  jobPackPrice,
  exportCreditPrice,
  downloadFree,
  followUpDownload,
  downloadPaid,
  buy,
  onSignedIn,
  onConfirmEmail,
  saveForLater,
  saveAndEdit,
  finishPack,
} = useCvOffers({
  cv: () => props.cvData,
  template: () => props.template,
  signupSource: props.signupSource,
  generationId: () => props.generationId,
  cvId: () => props.cvId,
  trackingData: () => props.trackingData,
  tailorToken: () => tailorDemo.preview?.tailorToken,
  confirmedSkills: () => tailorDemo.confirmed,
  skillLines: () => tailorDemo.skillLines,
})


/*
 * Right after the free download, while the visitor is still here and has just
 * been handed the general version: the offer to tailor it to their posting,
 * in seconds. Once per result, only where the pack can be bought; it replaces
 * the newsletter prompt that would otherwise follow the download, which comes
 * back if the panel is closed without a run.
 */
const showAfterDownload = ref(false)
let afterDownloadShown = false
const journey = useJourney()

/*
 * The free PDF goes down only once the visitor's e-mail address is confirmed.
 * The dialog (LandingEmailCvCard `gate`) sends a confirmation link; the click
 * on it (server/api/guest-cv/confirm.get.ts) proves the address and mails the
 * CV link, which opens this result again with the download free. This tab
 * asks the server (server/api/guest-cv/[id]/status.get.ts) when the download
 * is clicked and when the visitor comes back to it from their inbox.
 * Signed-in visitors and plans skip it, and so does a CV the e-mail cannot be
 * sent for (no longer stored, today's e-mail limit): the visitor did their part.
 */
const showEmailGate = ref(false)
/** The confirmation link went out to this address; the dialog waits for the click on it. */
const gateSentTo = ref('')
const emailConfirmed = ref(false)
const checkingEmail = ref(false)
/** "I've confirmed it" was clicked, but the server does not know yet. */
const gateNotYet = ref(false)

// A new CV (start over, edited answers) is confirmed on its own.
watch(() => props.generationId, () => {
  emailConfirmed.value = false
  gateSentTo.value = ''
  gateNotYet.value = false
})

async function checkEmailConfirmed(): Promise<boolean> {
  const id = props.generationId
  const sig = guestCvSig(id)
  if (emailConfirmed.value || !id || !sig) return emailConfirmed.value
  try {
    const status = await $fetch<{ emailConfirmed: boolean }>(`/api/guest-cv/${encodeURIComponent(id)}/status`, { query: { sig } })
    if (id === props.generationId) emailConfirmed.value = status.emailConfirmed
  } catch {
    // Not known: the dialog asks, and the link in the e-mail still opens the CV.
  }
  return emailConfirmed.value
}

async function onFreeDownload() {
  if (isAuthenticated.value || isPremium.value || !props.generationId) return downloadFreeNow()
  if (checkingEmail.value) return
  checkingEmail.value = true
  const confirmed = await checkEmailConfirmed()
  checkingEmail.value = false
  if (confirmed) return downloadFreeNow()
  gateNotYet.value = false
  showEmailGate.value = true
}

function onGateSent(email: string) {
  gateSentTo.value = email
  gateNotYet.value = false
}

function onGateUnsendable() {
  emailConfirmed.value = true
  showEmailGate.value = false
  downloadFreeNow()
}

async function downloadAfterConfirm() {
  if (checkingEmail.value) return
  gateNotYet.value = false
  checkingEmail.value = true
  const confirmed = await checkEmailConfirmed()
  checkingEmail.value = false
  if (!confirmed) {
    gateNotYet.value = true
    return
  }
  showEmailGate.value = false
  downloadFreeNow()
}

// Back from the inbox, where the link was clicked: the dialog turns into the download.
if (import.meta.client) {
  useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState === 'visible' && showEmailGate.value && gateSentTo.value && !emailConfirmed.value) checkEmailConfirmed()
  })
}

async function downloadFreeNow() {
  const offer = props.jobMatch && canBuyPack.value && !afterDownloadShown
  const ok = await downloadFree({ followUp: !offer })
  if (!ok || !offer) return
  afterDownloadShown = true
  showAfterDownload.value = true
  journey.track('cv_after_download_offer', {
    generation_id: props.generationId ?? null,
    tailored: !!tailorDemo.preview,
    ...props.trackingData,
  })
}

function closeAfterDownload() {
  if (tailorDemo.isRunning) return
  showAfterDownload.value = false
  if (!tailorDemo.preview) followUpDownload()
}

function buyFromTailor(plan: 'tailored-pdf' | 'application-kit' | 'job-pack') {
  showAfterDownload.value = false
  buy(plan)
}

/** Tailored to a posting: the interview offer right under it asks "the same posting or another?". */
const tailoredPosting = computed(() => (tailorDemo.preview && !tailorDemo.isRunning ? tailorDemo.posting : null))

/** The tailored PDF is paid for (alone or as the kit): the file went down on its own, the card offers it again. */
const tailoredPaid = computed(() => guestPaid.value && (guestPlan.value === 'tailored-pdf' || guestPlan.value === 'application-kit'))

/*
 * The finished tailoring, also in a wide dialog (the whole screen on a phone):
 * the card in the column is narrow to read on a desktop. It opens by itself
 * when a run from the card finishes, and the card's "Show larger" opens it
 * again. The after-download panel is a dialog already, so its runs do not.
 */
const showTailorResult = ref(false)
watch(() => tailorDemo.isRunning, (running, wasRunning) => {
  if (running || !wasRunning) return
  if (tailorDemo.preview && !showAfterDownload.value && !tailoredPaid.value) showTailorResult.value = true
})
// Another posting, or a purchase: the dialog has nothing more to show.
watch(() => [tailorDemo.preview, tailoredPaid.value] as const, ([preview, paid]) => {
  if (!preview || paid) showTailorResult.value = false
})

/** The checkout cannot show over the <dialog> (top layer): it closes first. */
function buyFromResult(plan: 'tailored-pdf' | 'application-kit' | 'job-pack') {
  showTailorResult.value = false
  buy(plan)
}

/*
 * The download in the preview's top corner (the builder shows it, see
 * `defineExpose`). Free where the PDF is clean anyway — Basic, a plan — or
 * where no clean PDF is on sale; otherwise it is the price of this one PDF,
 * and the click asks "just this one, or everything for the job search?".
 * The free watermarked PDF stays one link away in that choice.
 */
const singlePaid = computed(() => guestPaid.value && guestPlan.value === 'one-time')
const cornerIsFree = computed(() => isPremium.value || isBasic.value || !canBuySingle.value)
const cornerLabel = computed(() => {
  if (isPremium.value || singlePaid.value) return t('landing.builder.premiumDownload.cta')
  if (cornerIsFree.value) return t('landing.builder.corner.free')
  return t('landing.builder.corner.paid', { price: exportCreditPrice })
})
/** The download card's line: what this layout downloads as, and for how much. */
const downloadText = computed(() => {
  const name = t(`cv.templates.${props.template}.name`)
  if (isPremium.value || singlePaid.value) return t('landing.builder.download.owned', { name })
  if (isBasic.value) return t('landing.builder.download.basic', { name })
  if (cornerIsFree.value) return t('landing.builder.download.watermark', { name })
  return t('landing.builder.download.paid', { name })
})
const showDownloadChoice = ref(false)
/** The same dialog from the web CV preview: the website comes with the pack only. */
const choiceFor = ref<'download' | 'website'>('download')

function cornerDownload() {
  if (singlePaid.value) return downloadPaid()
  if (cornerIsFree.value) return downloadBasic()
  choiceFor.value = 'download'
  showDownloadChoice.value = true
}

/*
 * The free Basic download, the moment the visitor wants the file: once per
 * CV, before it goes down, their own CV in Basic (free, as promised) next to
 * the same CV in a designed layout (one clean PDF). Both buttons the same
 * size, so the free one is never hidden; the dialog does not come back for
 * the same CV, and plans or a CV paid for already skip it.
 */
const showDesignUpsell = ref(false)
const designUpsellSeen = new Set<string>()
/** The paid layouts, the offered one first; the arrows and the small pages under the preview step through them. */
const designTemplates = [DESIGN_UPSELL_TEMPLATE, ...PDF_TEMPLATES.filter(id => !isFreeCleanTemplate(id) && id !== DESIGN_UPSELL_TEMPLATE)]
const designTemplate = ref<PdfTemplateId>(DESIGN_UPSELL_TEMPLATE)
const designTemplateName = computed(() => t(`cv.templates.${designTemplate.value}.name`))
const designStripRef = ref<HTMLElement | null>(null)
/** Phones get the short button labels: the two pills share one row there too, under their cards. */
const isWide = useMediaQuery('(min-width: 640px)')
const freeDesignLabel = computed(() => (isWide.value
  ? t('landing.builder.designUpsell.freeCta', { name: t(`cv.templates.${FREE_CLEAN_TEMPLATE}.name`) })
  : t('landing.builder.designUpsell.freeCtaShort')))
const paidDesignLabel = computed(() => (isWide.value
  ? t('landing.builder.designUpsell.paidCta', { name: designTemplateName.value, price: exportCreditPrice })
  : t('landing.builder.designUpsell.paidCtaShort', { price: exportCreditPrice })))

function stepDesign(by: number) {
  const index = designTemplates.indexOf(designTemplate.value)
  pickDesign(designTemplates[(index + by + designTemplates.length) % designTemplates.length]!)
}

function pickDesign(id: PdfTemplateId) {
  designTemplate.value = id
  // The picked page stays in view in the strip, which scrolls on a phone
  nextTick(() => designStripRef.value?.querySelector<HTMLElement>(`[data-template="${id}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }))
}

function downloadBasic() {
  const key = props.generationId ?? 'cv'
  if (!isBasic.value || !canBuySingle.value || guestPaid.value || designUpsellSeen.has(key)) return onFreeDownload()
  designUpsellSeen.add(key)
  designTemplate.value = DESIGN_UPSELL_TEMPLATE
  showDesignUpsell.value = true
  journey.track('cv_design_upsell', { action: 'shown', generation_id: props.generationId ?? null, ...props.trackingData })
}

function designUpsellFree() {
  showDesignUpsell.value = false
  journey.track('cv_design_upsell', { action: 'free', generation_id: props.generationId ?? null, ...props.trackingData })
  onFreeDownload()
}

/** The designed layout becomes the one on screen, then its clean PDF is bought: what is paid for is what was shown. */
async function designUpsellBuy() {
  showDesignUpsell.value = false
  journey.track('cv_design_upsell', { action: 'buy', template: designTemplate.value, generation_id: props.generationId ?? null, ...props.trackingData })
  emit('pickTemplate', designTemplate.value)
  await nextTick()
  buy('one-time')
}

/** From the choice over a designed layout: the same CV in Basic, clean and free. */
async function basicFromChoice() {
  showDownloadChoice.value = false
  emit('pickTemplate', FREE_CLEAN_TEMPLATE)
  await nextTick()
  onFreeDownload()
}

/** "Make it online": a plan already has the web CV, in the editor; everyone else is offered the pack. */
const websiteLabel = computed(() => t(isPremium.value ? 'landing.builder.corner.websiteOwned' : 'landing.builder.corner.website'))
function cornerWebsite() {
  if (isPremium.value || !canBuyPack.value) return saveAndEdit()
  choiceFor.value = 'website'
  showDownloadChoice.value = true
}

function buyFromChoice(plan: 'one-time' | 'job-pack') {
  showDownloadChoice.value = false
  buy(plan)
}

function freeFromChoice() {
  showDownloadChoice.value = false
  onFreeDownload()
}

/*
 * C (`staged`): which of the three stages the panel is at. The CV is there
 * from the start; the posting stage is the tailoring running or shown; the
 * interview stage begins once the tailored CV is bought (the prep is offered
 * inside its card) or with a plan that has both.
 */
const STAGES = ['cv', 'tailor', 'interview'] as const
const stage = computed<'start' | 'tailor' | 'interview'>(() => {
  if (tailoredPaid.value || (isPremium.value && tailorDemo.preview)) return 'interview'
  if (tailorDemo.isRunning || tailorDemo.preview) return 'tailor'
  return 'start'
})
const stageIndex = computed(() => ({ start: 0, tailor: 1, interview: 2 })[stage.value])

/*
 * The stages are links too: "Posting" shows the tailoring alone, with the CV
 * ticked as done, and the CV is one click back. `null` follows the visitor's
 * real progress; any real step forward drops the pick.
 */
const pickedStage = ref<'start' | 'tailor' | null>(null)
const shownIndex = computed(() => pickedStage.value ? ({ start: 0, tailor: 1 })[pickedStage.value] : stageIndex.value)
watch(stage, () => { pickedStage.value = null })

function canPickStage(index: number) {
  return index === 0 || (index === 1 && props.jobMatch) || index <= stageIndex.value
}

function pickStage(index: number) {
  if (index === stageIndex.value) pickedStage.value = null
  else if (canPickStage(index) && index < 2) pickedStage.value = index === 0 ? 'start' : 'tailor'
}

/** The free download card: on its own once the CV is picked back from a later stage. */
const downloadShown = computed(() => {
  if (!props.jobMatch) return true
  if (pickedStage.value) return pickedStage.value === 'start'
  return stage.value === 'start' || tailorDemo.isRunning
})
/** The tailoring card stays mounted (it holds the posting); only hidden while the CV is picked back. */
const tailorShown = computed(() => pickedStage.value !== 'start' || stage.value === 'start')

/** The whole pack, opened from C's quiet line under the posting. */
const showPackOffer = ref(false)

/** The general CV from C's choice: free in Basic, as promised, whatever layout is on screen. */
function downloadGeneral() {
  showTailorResult.value = false
  if (isBasic.value) return onFreeDownload()
  return basicFromChoice()
}

/**
 * C's panel; the phone bar steps aside while the panel fills the screen: its
 * top in the upper half, or the screen anywhere inside it. Peeking in under
 * the CV does not count, the bar is what is read there.
 */
const stagedRef = ref<HTMLElement | null>(null)
const { top: stagedTop, bottom: stagedBottom } = useElementBounding(stagedRef)
const { height: windowHeight } = useWindowSize()
const stagedVisible = computed(() => !!stagedRef.value && stagedTop.value < windowHeight.value * 0.5 && stagedBottom.value > windowHeight.value * 0.3)

function toTailor() {
  pickedStage.value = null
  tailorCard.value?.openForm()
  nextTick(() => document.getElementById('cv-tailor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

/** The bar's one button: the same next step as the panel's. */
const barAction = computed<{ label: string, note?: string, paid?: boolean, busy?: boolean, run: () => void } | null>(() => {
  if (props.layout !== 'staged' || packAwaitingAccount.value || tailoredPaid.value) return null
  if (tailorDemo.isRunning) return { label: t('landing.builder.staged.bar.running'), busy: true, run: () => {} }
  const preview = tailorDemo.preview
  if (preview && props.jobMatch) {
    return {
      note: preview.matchScore !== tailorDemo.potentialScore
        ? t('landing.builder.staged.bar.score', { before: preview.matchScore, after: tailorDemo.potentialScore })
        : undefined,
      label: t('landing.builder.staged.bar.choose'),
      paid: true,
      run: () => document.getElementById('cv-tailor-choice')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    }
  }
  // The download is in the CV's corner right above: the bar is the step after it
  if (!props.jobMatch) return null
  return { note: t('landing.builder.staged.bar.tailorNote'), label: t('landing.builder.staged.bar.tailor'), run: toTailor }
})

/*
 * Keeping the CV to edit later always goes through an e-mail address: signed
 * out, the visitor gives one first (the download's gate, `purpose: 'save'`),
 * the CV's link goes there, and the marketing consent is asked next to it,
 * unticked; or they pick the account right away, whose sign-up asks for the
 * same. After the e-mail the account is offered too, now or later. An address
 * given for the download already counts. Signed in, the editor opens at once.
 */
const showSaveGate = ref(false)
const saveSentTo = ref('')

function saveLater() {
  if (isAuthenticated.value) return saveAndEdit()
  if (!props.generationId || emailConfirmed.value || gateSentTo.value || saveSentTo.value) return saveForLater()
  showSaveGate.value = true
}

function onSaveSent(email: string) {
  saveSentTo.value = email
  // The same confirmation opens the free download: its gate waits for that click, not for another address.
  gateSentTo.value ||= email
}

/** The e-mail cannot go out (today's limit, a CV no longer stored): straight to the account, as before. */
function onSaveUnsendable() {
  showSaveGate.value = false
  saveForLater()
}

function accountFromSaveGate() {
  showSaveGate.value = false
  saveForLater()
}

// A new CV (start over, edited answers) is kept on its own.
watch(() => props.generationId, () => {
  saveSentTo.value = ''
})

/** Save the CV and open the editor; signed out, an e-mail address first, then a free account. */
function editCv() {
  return saveLater()
}

/** The tailoring card on the page (not the one after the free download). */
const tailorCard = ref<{ openForm: () => void } | null>(null)

defineExpose({
  /** Open the tailoring card's posting field, ready for a posting. */
  openTailor: () => tailorCard.value?.openForm(),
  /** Show a tailoring run earlier again (the day-0 e-mail's link). */
  restoreTailor: (preview: TailorPreview, posting: string | null) => {
    tailorDemo.restore(preview, posting)
    showTailorResult.value = true
  },
  /** The posting from the e-mail link, in the field and not run (the card's button runs it). */
  offerTailor: (posting: string) => givePosting(posting.trim()),
  /** The corner button's text: "Download free", "Download for $4.90"… */
  cornerLabel,
  /** Busy preparing a PDF or a checkout: the corner button waits. */
  cornerBusy: computed(() => isExporting.value || isStartingCheckout.value),
  cornerPaid: computed(() => !cornerIsFree.value && !singlePaid.value),
  cornerDownload,
  websiteLabel,
  cornerWebsite,
  /* The CV review over the result (LandingCvReview) buys and edits through here. */
  canBuyPack,
  buyingPack: computed(() => isStartingCheckout.value || isScriptLoading.value),
  buyPack: () => buy('job-pack'),
  editCv,
})
</script>

<template>
  <div class="flex flex-col gap-4 text-left">
    <!-- A (chat_result_v1) and every other surface: the CV kept to edit, then the offers -->
    <template v-if="layout === 'classic'">
      <!--
        Keeping the CV is a step, not a price, so it opens the column above the
        offers: the visitor who wants to fix a line first should not have to read
        past three cards to find the editor. Quieter than the cards below it —
        smaller type, a thin frame instead of the pack's dark one — so the pack
        still leads on price, but on its own white surface and in body colour so
        it does not fade into the dotted backdrop.
      -->
      <div class="rounded-[0.625rem] border border-line bg-white p-5 shadow-card dark:border-gray-700 dark:bg-gray-900">
        <div class="mb-1 flex items-start justify-between gap-4">
          <div class="font-title text-lg font-bold text-ink dark:text-white">
            {{ isAuthenticated ? t('homepage.aiGenerator.saveAndEdit') : t('homepage.hero.result.saveTitle') }}
          </div>
          <i class="bi bi-pencil-square shrink-0 text-4xl leading-none text-lilac" aria-hidden="true" />
        </div>
        <p class="mb-4 !text-sm !text-body dark:!text-gray-300">
          {{ isAuthenticated
            ? t('homepage.hero.result.saveSignedInText')
            : t('homepage.hero.result.saveText', { price: jobPackPrice }) }}
        </p>
        <SofaxBtn
          v-if="isAuthenticated"
          variant="outline"
          block
          :label="t('homepage.aiGenerator.saveAndEdit')"
          @click="saveAndEdit"
        />
        <SofaxBtn
          v-else
          variant="outline"
          block
          :label="t('homepage.aiGenerator.saveAndEditLater')"
          @click="saveLater"
        />
        <!-- The photo is added in the editor, not on the result: one quiet line, not a box -->
        <button
          type="button"
          class="mt-3 inline-flex items-center gap-1.5 !text-sm text-muted underline-offset-4 hover:text-ink hover:underline dark:text-gray-400 dark:hover:text-white"
          @click="saveLater"
        >
          <i class="bi bi-camera" aria-hidden="true" />
          {{ isAuthenticated ? t('homepage.hero.result.photoSignedIn') : t('homepage.hero.result.photo') }}
        </button>
      </div>

      <!-- The turn from "the CV is done" to what gets the job: a question over the offers -->
      <div v-if="!packAwaitingAccount" class="mt-2">
        <h3 class="font-title !text-xl font-bold text-ink md:!text-2xl dark:text-white">{{ t('landing.builder.next.title') }}</h3>
        <p class="mt-1 !text-sm text-body dark:text-gray-300">{{ t('landing.builder.next.text') }}</p>
      </div>

      <!-- Their CV rewritten for the job they want, in seconds: the pack below applies it -->
      <LandingTailorDemo
        v-if="jobMatch && !packAwaitingAccount"
        id="cv-tailor"
        ref="tailorCard"
        class="scroll-mt-24"
        :demo="tailorDemo"
        :cv-data="cvData"
        :offer-posting="offersGivenPosting && canBuyPack"
        :can-buy-pack="canBuyPack"
        :is-buying="isStartingCheckout || isScriptLoading"
        :can-buy-tailored="canBuyTailored"
        :can-buy-kit="canBuyKit"
        :tailored-paid="tailoredPaid"
        :tailored-download-url="tailoredPaid ? guestDownloadUrl : null"
        :tailored-token="tailoredPaid ? guestToken : null"
        :is-exporting="isExporting"
        expandable
        @buy="buy('job-pack')"
        @buy-tailored="buy('tailored-pdf')"
        @buy-kit="buy('application-kit')"
        @download-tailored="downloadPaid"
        @edit="saveAndEdit"
        @expand="showTailorResult = true"
      />

      <!-- Right under the tailoring: the interview for the job they apply to.
           Open to everyone without a plan that has it, a free Basic download included;
           the follow-up e-mail links here (?focus=interview). A paid tailored PDF
           already offers it inside the tailoring card. -->
      <InterviewPrepOffer
        v-if="!tailoredPaid && !packAwaitingAccount"
        id="cv-interview"
        class="scroll-mt-24"
        :known-posting="tailoredPosting"
        :cv-data="cvData"
        :generation-id="generationId"
        :template="template"
        @buy-pack="buy('job-pack')"
      />

      <!-- Paid route: the template's highlighted pricing card -->
      <div v-if="canBuyPack" class="card !border-ink dark:!border-white">
        <div class="mb-4 flex items-start justify-between gap-4">
          <span class="tag">{{ t('landing.builder.pack.badge') }}</span>
          <i class="bi bi-rocket-takeoff shrink-0 text-4xl leading-none text-lilac" aria-hidden="true" />
        </div>

        <!-- Outcome and price first, the lines, the button, then the fine print -->
        <PremiumJobPackStack layout="offer" :lead="packLead" :headline="packHeadline" :sub="packSub">
          <template #cta>
            <!-- Paid, and now it only needs somewhere to live: the account is asked for
                 after the money, never in front of it. -->
            <template v-if="packAwaitingAccount">
              <SofaxBtn
                variant="accent"
                block
                :disabled="isClaiming"
                :label="isClaiming ? t('common.loading') : t('landing.builder.pack.claimCta')"
                @click="finishPack"
              />
              <SofaxNotice type="success" class="mt-4">
                <div class="font-semibold text-ink dark:text-white">{{ t('landing.builder.pack.claimTitle') }}</div>
                <p class="mt-1 text-sm">{{ t('landing.builder.pack.claimText') }}</p>
                <a v-if="guestClaimUrl" :href="guestClaimUrl" class="link mt-2 inline-block !text-sm">
                  {{ t('landing.builder.pack.claimLink') }}
                </a>
              </SofaxNotice>
            </template>

            <template v-else>
              <SofaxBtn
                variant="accent"
                block
                :disabled="isStartingCheckout || isScriptLoading"
                :label="isStartingCheckout && checkoutPlanId === 'job-pack' ? t('common.loading') : t('premium.jobPack.cta', { price: packCredit.packPrice.value })"
                @click="buy('job-pack')"
              />
              <p v-if="packCredit.creditText.value" class="mt-3 text-center text-sm font-semibold text-accent-strong dark:text-accent-soft">
                {{ t('premium.jobPack.creditNote', { credit: packCredit.creditText.value }) }}
              </p>
              <p class="mt-3 text-center text-sm text-muted dark:text-gray-400">
                {{ isAuthenticated ? t('landing.builder.pack.noRenew') : t('landing.builder.pack.noAccount') }}
              </p>

              <!-- The free PDF is the promise that got the visitor here, and its own card
                   sits at the bottom of the column. This keeps it one tap away. -->
              <p v-if="freeDownload" class="mt-4 text-center">
                <button
                  type="button"
                  class="link !text-sm disabled:opacity-50"
                  :disabled="isExporting"
                  @click="isBasic ? downloadBasic() : onFreeDownload()"
                >
                  {{ isExporting ? t('landing.builder.free.preparing') : t(isBasic ? 'landing.builder.pack.orFreeBasic' : 'landing.builder.pack.orFree') }}
                </button>
              </p>
            </template>
          </template>
        </PremiumJobPackStack>
      </div>

    </template>

    <!-- B: the result first, then numbered steps, then the pack they add up to -->
    <template v-else-if="layout === 'path'">
      <!--
        The result first: the CV they came for, downloaded where it was promised,
        free in Basic. The value is handed over before anything is sold, and the
        download itself is where the upsells start (the designed layout before
        it, the tailoring right after it). Keeping it to edit is the step next to
        it, not a card of its own.
      -->
      <div id="cv-download" class="card scroll-mt-24 !p-5 md:!p-6">
        <div class="flex items-start gap-4">
          <span class="flex size-12 shrink-0 items-center justify-center rounded-full bg-accent/15 text-2xl text-accent-strong dark:text-accent-soft" aria-hidden="true">
            <i class="bi bi-file-earmark-arrow-down" />
          </span>
          <div class="min-w-0 grow">
            <div class="flex flex-wrap items-baseline justify-between gap-x-3">
              <div class="font-title text-lg font-bold text-ink dark:text-white">{{ t('landing.builder.download.title') }}</div>
              <span v-if="!isPremium && !singlePaid" class="font-title text-base font-bold" :class="cornerIsFree ? 'text-accent-strong dark:text-accent-soft' : 'text-ink dark:text-white'">
                {{ cornerIsFree ? t('cvChat.templates.free') : exportCreditPrice }}
              </span>
            </div>
            <p class="mt-0.5 !text-sm text-body dark:text-gray-300">{{ downloadText }}</p>
          </div>
        </div>
        <SofaxBtn
          class="mt-4"
          :variant="cornerIsFree ? 'dark' : 'accent'"
          block
          :disabled="isExporting || isStartingCheckout || checkingEmail"
          :label="isExporting ? t('landing.builder.free.preparing') : cornerLabel"
          @click="cornerDownload"
        />
        <p v-if="!cornerIsFree && !singlePaid && freeDownload" class="mt-2 text-center">
          <button type="button" class="link !text-sm" :disabled="isExporting" @click="basicFromChoice">{{ t('landing.builder.corner.basicFree') }}</button>
        </p>
        <div class="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-line pt-4 dark:border-gray-700">
          <button
            type="button"
            class="inline-flex items-center gap-1.5 !text-sm font-semibold text-ink underline-offset-4 hover:underline dark:text-white"
            @click="editCv"
          >
            <i class="bi bi-pencil-square" aria-hidden="true" />
            {{ isAuthenticated ? t('homepage.aiGenerator.saveAndEdit') : t('homepage.aiGenerator.saveAndEditLater') }}
          </button>
          <!-- The photo is added in the editor, not on the result -->
          <button
            type="button"
            class="inline-flex items-center gap-1.5 !text-sm text-muted underline-offset-4 hover:text-ink hover:underline dark:text-gray-400 dark:hover:text-white"
            @click="editCv"
          >
            <i class="bi bi-camera" aria-hidden="true" />
            {{ t('landing.builder.download.photo') }}
          </button>
        </div>
      </div>

      <!-- The turn from "the CV is done" to what gets the interview: numbered steps, each tried for free first -->
      <div v-if="!packAwaitingAccount && (jobMatch || canBuyPack)" class="mt-4">
        <h3 class="font-title !text-xl font-bold text-ink md:!text-2xl dark:text-white">{{ t('landing.builder.next.pathTitle') }}</h3>
        <p class="mt-1 !text-sm text-body dark:text-gray-300">{{ t('landing.builder.next.pathText') }}</p>
      </div>

      <!-- Their CV rewritten for the job they want, in seconds: the pack below applies it -->
      <LandingTailorDemo
        v-if="jobMatch && !packAwaitingAccount"
        id="cv-tailor"
        ref="tailorCard"
        class="scroll-mt-24"
        :eyebrow="t('landing.builder.next.step', { n: 2, what: isPremium ? t('landing.builder.next.included') : t('landing.builder.next.freePreview') })"
        :demo="tailorDemo"
        :cv-data="cvData"
        :offer-posting="offersGivenPosting && canBuyPack"
        :can-buy-pack="canBuyPack"
        :is-buying="isStartingCheckout || isScriptLoading"
        :can-buy-tailored="canBuyTailored"
        :can-buy-kit="canBuyKit"
        :tailored-paid="tailoredPaid"
        :tailored-download-url="tailoredPaid ? guestDownloadUrl : null"
        :tailored-token="tailoredPaid ? guestToken : null"
        :is-exporting="isExporting"
        expandable
        @buy="buy('job-pack')"
        @buy-tailored="buy('tailored-pdf')"
        @buy-kit="buy('application-kit')"
        @download-tailored="downloadPaid"
        @edit="saveAndEdit"
        @expand="showTailorResult = true"
      />

      <!-- Right under the tailoring: the interview for the job they apply to.
           Open to everyone without a plan that has it, a free Basic download included;
           the follow-up e-mail links here (?focus=interview). A paid tailored PDF
           already offers it inside the tailoring card. -->
      <InterviewPrepOffer
        v-if="!tailoredPaid && !packAwaitingAccount"
        id="cv-interview"
        class="scroll-mt-24"
        :eyebrow="t('landing.builder.next.step', { n: jobMatch ? 3 : 2, what: t('landing.builder.next.freePreview') })"
        :known-posting="tailoredPosting"
        :cv-data="cvData"
        :generation-id="generationId"
        :template="template"
        @buy-pack="buy('job-pack')"
      />

      <!-- Everything above, for every posting: the offer the steps add up to -->
      <LandingPackOffer
        v-if="canBuyPack"
        id="cv-pack"
        class="scroll-mt-24"
        :price="packCredit.packPrice.value"
        :headline="packHeadline"
        :sub="packSub"
        :bought="packAwaitingAccount"
      >
        <template #cta>
          <!-- Paid, and now it only needs somewhere to live: the account is asked for
               after the money, never in front of it. -->
          <template v-if="packAwaitingAccount">
            <SofaxBtn
              variant="accent"
              block
              :disabled="isClaiming"
              :label="isClaiming ? t('common.loading') : t('landing.builder.pack.claimCta')"
              @click="finishPack"
            />
            <SofaxNotice type="success" class="mt-4">
              <div class="font-semibold text-ink dark:text-white">{{ t('landing.builder.pack.claimTitle') }}</div>
              <p class="mt-1 text-sm">{{ t('landing.builder.pack.claimText') }}</p>
              <a v-if="guestClaimUrl" :href="guestClaimUrl" class="link mt-2 inline-block !text-sm">
                {{ t('landing.builder.pack.claimLink') }}
              </a>
            </SofaxNotice>
          </template>

          <template v-else>
            <SofaxBtn
              variant="accent"
              block
              :disabled="isStartingCheckout || isScriptLoading"
              :label="isStartingCheckout && checkoutPlanId === 'job-pack' ? t('common.loading') : t('landing.builder.packOffer.cta', { price: packCredit.packPrice.value })"
              @click="buy('job-pack')"
            />
            <p v-if="packCredit.creditText.value" class="mt-3 text-center text-sm font-semibold text-accent-strong dark:text-accent-soft">
              {{ t('premium.jobPack.creditNote', { credit: packCredit.creditText.value }) }}
            </p>
            <!-- What stops a buyer: a subscription, an account, paying blind -->
            <ul class="mt-4 grid gap-1.5 !text-sm text-body sm:grid-cols-3 sm:gap-2 dark:text-gray-300">
              <li v-for="item in ['once', 'noAccount', 'keep']" :key="item" class="flex items-start gap-1.5 sm:flex-col sm:items-center sm:text-center">
                <i class="bi bi-shield-check shrink-0 text-accent" aria-hidden="true" />
                <span>{{ t(`landing.builder.packOffer.trust.${isAuthenticated && item === 'noAccount' ? 'account' : item}`) }}</span>
              </li>
            </ul>
          </template>
        </template>
      </LandingPackOffer>

    </template>

    <!--
      C: one step at a time. Only the next thing to do is on the panel: the
      free download with the posting field right under it, then the tailoring
      as it runs, then one choice (the pack, this CV tailored, or the general
      CV free). The interview prep is offered once a tailored CV is bought,
      inside the tailoring card. On phones a bar at the bottom of the screen
      carries the same next step while the CV is read.
    -->
    <div v-else ref="stagedRef" class="flex flex-col gap-4">
      <!-- Where they are: three stages, the shown one dark, the ones done green; each one reached opens its card -->
      <ol v-if="!packAwaitingAccount" class="grid grid-cols-3 gap-1.5" :aria-label="t('landing.builder.staged.label')">
        <li v-for="(id, index) in STAGES" :key="id" class="min-w-0">
          <button
            type="button"
            class="flex w-full items-center gap-2 rounded-full border px-2 py-1.5 text-left !text-xs font-semibold transition-colors disabled:cursor-default"
            :class="index === shownIndex
              ? 'border-ink bg-ink !text-white dark:border-white dark:bg-white dark:!text-ink'
              : index < shownIndex || index < stageIndex
                ? 'border-accent/40 bg-accent/10 text-accent-strong hover:border-accent dark:text-accent-soft'
                : 'border-line bg-white text-muted enabled:hover:border-ink enabled:hover:text-ink dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:enabled:hover:border-white/60'"
            :disabled="index === shownIndex || !canPickStage(index)"
            :aria-current="index === shownIndex ? 'step' : undefined"
            @click="pickStage(index)"
          >
            <span
              class="flex size-5 shrink-0 items-center justify-center rounded-full"
              :class="index === shownIndex ? 'bg-white/20' : index < shownIndex || index < stageIndex ? 'bg-accent !text-white' : 'bg-surface dark:bg-gray-700'"
              aria-hidden="true"
            >
              <i v-if="index !== shownIndex && (index < shownIndex || index < stageIndex)" class="bi bi-check-lg" />
              <template v-else>{{ index + 1 }}</template>
            </span>
            <span class="truncate">{{ t(`landing.builder.staged.stages.${id}`) }}</span>
          </button>
        </li>
      </ol>

      <!-- Paid for the pack: only the account is left -->
      <LandingPackOffer v-if="packAwaitingAccount" :price="packCredit.packPrice.value" bought>
        <template #cta>
          <SofaxBtn
            variant="accent"
            block
            :disabled="isClaiming"
            :label="isClaiming ? t('common.loading') : t('landing.builder.pack.claimCta')"
            @click="finishPack"
          />
          <SofaxNotice type="success" class="mt-4">
            <div class="font-semibold text-ink dark:text-white">{{ t('landing.builder.pack.claimTitle') }}</div>
            <p class="mt-1 text-sm">{{ t('landing.builder.pack.claimText') }}</p>
            <a v-if="guestClaimUrl" :href="guestClaimUrl" class="link mt-2 inline-block !text-sm">
              {{ t('landing.builder.pack.claimLink') }}
            </a>
          </SofaxNotice>
        </template>
      </LandingPackOffer>

      <template v-else>
        <!-- 1. The CV, free. Once a tailored version is on screen, the choice under it has the free one too. -->
        <div v-if="downloadShown" id="cv-download" class="card scroll-mt-24 !p-5">
          <div class="flex items-baseline justify-between gap-3">
            <div class="font-title text-lg font-bold text-ink dark:text-white">{{ t('landing.builder.download.title') }}</div>
            <span v-if="!isPremium && !singlePaid" class="shrink-0 font-title text-base font-bold" :class="cornerIsFree ? 'text-accent-strong dark:text-accent-soft' : 'text-ink dark:text-white'">
              {{ cornerIsFree ? t('cvChat.templates.free') : exportCreditPrice }}
            </span>
          </div>
          <p class="mt-0.5 !text-sm text-body dark:text-gray-300">{{ downloadText }}</p>
          <SofaxBtn
            class="mt-3"
            :variant="cornerIsFree ? 'dark' : 'accent'"
            block
            :disabled="isExporting || isStartingCheckout || checkingEmail"
            :label="isExporting ? t('landing.builder.free.preparing') : cornerLabel"
            @click="cornerDownload"
          />
          <div class="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <button type="button" class="inline-flex items-center gap-1.5 !text-sm font-semibold text-ink underline-offset-4 hover:underline dark:text-white" @click="editCv">
              <i class="bi bi-pencil-square" aria-hidden="true" />
              {{ isAuthenticated ? t('homepage.aiGenerator.saveAndEdit') : t('homepage.aiGenerator.saveAndEditLater') }}
            </button>
            <button v-if="!cornerIsFree && !singlePaid && freeDownload" type="button" class="link !text-sm" :disabled="isExporting" @click="basicFromChoice">
              {{ t('landing.builder.staged.basicFree') }}
            </button>
          </div>
        </div>

        <!-- 2. The posting, its field open at once: the next step is one paste away -->
        <LandingTailorDemo
          v-if="jobMatch"
          v-show="tailorShown"
          id="cv-tailor"
          ref="tailorCard"
          class="scroll-mt-24"
          :eyebrow="stage === 'start' ? t('landing.builder.next.step', { n: 2, what: isPremium ? t('landing.builder.next.included') : t('landing.builder.next.freePreview') }) : undefined"
          :demo="tailorDemo"
          :cv-data="cvData"
          :offer-posting="offersGivenPosting && canBuyPack"
          :can-buy-pack="canBuyPack"
          :is-buying="isStartingCheckout || isScriptLoading"
          :can-buy-tailored="canBuyTailored"
          :can-buy-kit="canBuyKit"
          :tailored-paid="tailoredPaid"
          :tailored-download-url="tailoredPaid ? guestDownloadUrl : null"
          :tailored-token="tailoredPaid ? guestToken : null"
          :is-exporting="isExporting"
          start-open
          tiers
          expandable
          @buy="buy('job-pack')"
          @buy-tailored="buy('tailored-pdf')"
          @buy-kit="buy('application-kit')"
          @download-free="downloadGeneral"
          @download-tailored="downloadPaid"
          @edit="saveAndEdit"
          @expand="showTailorResult = true"
        />

        <!-- No posting yet: the pack stays one quiet line away, not a card that competes with the next step -->
        <button
          v-if="canBuyPack && stage === 'start'"
          type="button"
          class="flex items-center gap-3 rounded-[0.625rem] border border-dashed border-line px-4 py-3 text-left transition-colors hover:border-ink dark:border-gray-700 dark:hover:border-white/60"
          @click="showPackOffer = true"
        >
          <i class="bi bi-rocket-takeoff-fill shrink-0 text-xl text-lilac" aria-hidden="true" />
          <span class="min-w-0 grow !text-sm text-body dark:text-gray-300">
            <span class="font-semibold text-ink dark:text-white">{{ t('landing.builder.staged.packTitle') }}</span>
            {{ t('landing.builder.staged.packText', { price: packCredit.packPrice.value }) }}
          </span>
          <i class="bi bi-chevron-right shrink-0 text-muted" aria-hidden="true" />
        </button>
      </template>

      <!-- The whole pack, when asked for -->
      <CommonDialog
        v-if="canBuyPack && !packAwaitingAccount"
        v-model="showPackOffer"
        :title="t('premium.jobPack.title')"
        size="2xl"
        fullscreen-mobile
        :footer="false"
      >
        <LandingPackOffer :price="packCredit.packPrice.value" :headline="packHeadline" :sub="packSub">
          <template #cta>
            <SofaxBtn
              variant="accent"
              block
              :disabled="isStartingCheckout || isScriptLoading"
              :label="t('landing.builder.packOffer.cta', { price: packCredit.packPrice.value })"
              @click="showPackOffer = false; buy('job-pack')"
            />
            <p v-if="packCredit.creditText.value" class="mt-3 text-center text-sm font-semibold text-accent-strong dark:text-accent-soft">
              {{ t('premium.jobPack.creditNote', { credit: packCredit.creditText.value }) }}
            </p>
            <p class="mt-3 text-center text-sm text-muted dark:text-gray-400">
              {{ isAuthenticated ? t('landing.builder.pack.noRenew') : t('landing.builder.pack.noAccount') }}
            </p>
          </template>
        </LandingPackOffer>
      </CommonDialog>

      <!-- Phones: the next step at the bottom of the screen while the CV is read; gone once the panel is in view -->
      <Teleport to="body">
        <Transition
          enter-from-class="translate-y-full"
          leave-to-class="translate-y-full"
          enter-active-class="transition duration-300"
          leave-active-class="transition duration-200"
        >
          <div
            v-if="barAction && !stagedVisible"
            class="result-bar sofax fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-lift backdrop-blur lg:hidden dark:border-gray-700 dark:bg-gray-900/95"
          >
            <div class="mx-auto flex max-w-md items-center gap-3">
              <span v-if="barAction.note" class="min-w-0 grow !text-sm font-semibold leading-tight text-ink dark:text-white">{{ barAction.note }}</span>
              <SofaxBtn
                :variant="barAction.paid ? 'accent' : 'dark'"
                :block="!barAction.note"
                class="shrink-0"
                :disabled="barAction.busy"
                :label="barAction.label"
                @click="barAction.run()"
              />
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>

    <!-- The same result, wide: easier to read than the column on a desktop -->
    <CommonDialog
      v-if="jobMatch && !packAwaitingAccount && tailorDemo.preview && !tailoredPaid"
      v-model="showTailorResult"
      :title="t('landing.builder.jobMatch.title')"
      size="4xl"
      fullscreen-mobile
      :footer="false"
    >
      <LandingTailorDemo
        :demo="tailorDemo"
        :cv-data="cvData"
        :can-buy-pack="canBuyPack"
        :is-buying="isStartingCheckout || isScriptLoading"
        :can-buy-tailored="canBuyTailored"
        :can-buy-kit="canBuyKit"
        :is-exporting="isExporting"
        :tiers="layout === 'staged'"
        bare
        @buy="buyFromResult('job-pack')"
        @buy-tailored="buyFromResult('tailored-pdf')"
        @buy-kit="buyFromResult('application-kit')"
        @download-free="downloadGeneral"
        @edit="showTailorResult = false; saveAndEdit()"
      />
    </CommonDialog>

    <!-- Single clean PDF, for people who need just this one file; C sells it from the download -->
    <div v-if="canBuySingle && !isBasic && layout !== 'staged'" class="card">
      <div class="flex flex-wrap items-baseline justify-between gap-x-3">
        <div class="font-title text-xl font-bold text-ink dark:text-white">
          {{ t('landing.builder.single.title') }}
        </div>
        <span class="font-title text-xl font-bold text-ink dark:text-white">{{ exportCreditPrice }}</span>
      </div>
      <p class="mb-5 mt-1 text-base">{{ t('landing.builder.single.description') }}</p>

      <!-- Paid: the file is already on its way down, this is the repeat button -->
      <template v-if="guestPaid && guestPlan === 'one-time'">
        <SofaxBtn
          variant="dark"
          block
          :disabled="isExporting"
          :label="isExporting ? t('landing.builder.free.preparing') : t('landing.builder.single.paid.cta')"
          @click="downloadPaid"
        />
        <SofaxNotice type="success" class="mt-4">
          <div class="font-semibold text-ink dark:text-white">{{ t('landing.builder.single.paid.title') }}</div>
          <p class="mt-1 text-sm">{{ t('landing.builder.single.paid.description') }}</p>
          <a v-if="guestDownloadUrl" :href="guestDownloadUrl" class="link mt-2 inline-block !text-sm">
            {{ t('landing.builder.single.paid.link') }}
          </a>
        </SofaxNotice>
      </template>

      <template v-else>
        <SofaxBtn
          variant="accent"
          block
          :disabled="isStartingCheckout || isScriptLoading"
          :label="isStartingCheckout && checkoutPlanId === 'one-time' ? t('common.loading') : t('landing.builder.single.cta', { price: exportCreditPrice })"
          @click="buy('one-time')"
        />
        <p class="mt-3 text-center text-sm text-muted dark:text-gray-400">
          {{ t('landing.builder.single.noAccount') }}
        </p>
      </template>
    </div>

    <!-- The download itself is the button in the preview's corner (cornerDownload):
         free, paid or covered by a plan, it is one place, next to the CV. -->

    <!-- Confirmation notice when the account needs an e-mail click first -->
    <SofaxNotice v-if="confirmEmailSentTo" type="info">
      <div class="font-semibold text-ink dark:text-white">{{ t('landing.builder.confirmEmail.title') }}</div>
      <p class="mt-1 text-sm">
        {{ t(
          packAwaitingAccount ? 'landing.builder.confirmEmail.claimDescription' : 'landing.builder.confirmEmail.description',
          { email: confirmEmailSentTo },
        ) }}
      </p>
    </SofaxNotice>

    <SofaxNotice v-if="error" type="error">{{ error }}</SofaxNotice>

    <!-- The page adds its own links here, e.g. "start over" -->
    <div v-if="$slots.links" class="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
      <slot name="links" />
    </div>

    <LandingQuickSignupDialog
      v-model="showSignup"
      :offer-label="signupOfferLabel"
      :claim="packAwaitingAccount"
      :claim-token="packAwaitingAccount ? (guestToken ?? undefined) : undefined"
      @signed-in="onSignedIn"
      @confirm-email="onConfirmEmail"
    />

    <!-- After the free download: the general version is on its way, now the tailored one -->
    <Teleport to="body">
      <div
        v-if="showAfterDownload"
        class="fixed inset-0 z-[1200] flex items-end justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-4"
        @click.self="closeAfterDownload"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="after-download-title"
          class="sofax max-h-[92svh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 shadow-lift sm:rounded-[0.625rem] sm:p-8 dark:bg-gray-800"
        >
          <button
            type="button"
            class="float-right -mr-2 -mt-2 inline-flex size-9 items-center justify-center rounded-full text-ink hover:bg-surface disabled:opacity-40 dark:text-white dark:hover:bg-gray-700"
            :disabled="tailorDemo.isRunning"
            :aria-label="t('common.close')"
            @click="closeAfterDownload"
          >
            <i class="bi bi-x-lg" />
          </button>
          <div class="mb-2 flex items-center gap-2 text-sm font-semibold text-accent-strong dark:text-accent-soft">
            <i class="bi bi-check-circle-fill" aria-hidden="true" />
            {{ t('landing.builder.afterDownload.downloaded') }}
          </div>
          <h3 id="after-download-title" class="mb-2 !text-2xl">
            {{ tailorDemo.preview ? t('landing.builder.afterDownload.readyTitle') : t('landing.builder.afterDownload.title') }}
          </h3>
          <p v-if="!tailorDemo.isRunning" class="!mb-5 text-base">
            {{ tailorDemo.preview ? t('landing.builder.afterDownload.readyText') : t('landing.builder.afterDownload.text') }}
          </p>
          <LandingTailorDemo
            :demo="tailorDemo"
            :cv-data="cvData"
            :can-buy-pack="canBuyPack"
            :is-buying="isStartingCheckout || isScriptLoading"
            :can-buy-tailored="canBuyTailored"
            :can-buy-kit="canBuyKit"
            :tailored-paid="tailoredPaid"
            :tailored-download-url="tailoredPaid ? guestDownloadUrl : null"
            :is-exporting="isExporting"
            where="after_download"
            bare
            @buy="buyFromTailor('job-pack')"
            @buy-tailored="buyFromTailor('tailored-pdf')"
            @buy-kit="buyFromTailor('application-kit')"
            @download-tailored="downloadPaid"
            @edit="saveAndEdit"
          />
          <p v-if="!tailorDemo.isRunning" class="mt-4 text-center">
            <button type="button" class="link !text-sm" @click="closeAfterDownload">{{ t('landing.builder.afterDownload.later') }}</button>
          </p>
        </div>
      </div>
    </Teleport>

    <!-- From the preview's corner: this one PDF, or everything for the job search -->
    <Teleport to="body">
      <div
        v-if="showDownloadChoice"
        class="fixed inset-0 z-[1200] flex items-end justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-4"
        @click.self="showDownloadChoice = false"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="download-choice-title"
          class="sofax max-h-[92svh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 shadow-lift sm:rounded-[0.625rem] sm:p-8 dark:bg-gray-800"
          @keydown.esc="showDownloadChoice = false"
        >
          <button
            type="button"
            class="float-right -mr-2 -mt-2 inline-flex size-9 items-center justify-center rounded-full text-ink hover:bg-surface dark:text-white dark:hover:bg-gray-700"
            :aria-label="t('common.close')"
            @click="showDownloadChoice = false"
          >
            <i class="bi bi-x-lg" />
          </button>
          <h3 id="download-choice-title" class="!text-2xl" :class="choiceFor === 'website' ? 'mb-2' : 'mb-5'">
            {{ choiceFor === 'website' ? t('landing.builder.corner.websiteTitle') : t('landing.builder.corner.title') }}
          </h3>
          <p v-if="choiceFor === 'website'" class="!mb-5 !text-base">{{ t('landing.builder.corner.websiteText') }}</p>

          <div class="space-y-3">
            <!-- Just this CV -->
            <div v-if="choiceFor === 'download'" class="rounded-[0.625rem] border border-line p-4 dark:border-gray-700">
              <div class="flex items-baseline justify-between gap-3">
                <div class="font-title text-lg font-bold text-ink dark:text-white">{{ t('landing.builder.single.title') }}</div>
                <span class="font-title text-lg font-bold text-ink dark:text-white">{{ exportCreditPrice }}</span>
              </div>
              <p class="mb-4 mt-1 !text-sm">{{ t('landing.builder.single.description') }}</p>
              <SofaxBtn
                variant="accent"
                block
                :disabled="isStartingCheckout || isScriptLoading"
                :label="t('landing.builder.single.cta', { price: exportCreditPrice })"
                @click="buyFromChoice('one-time')"
              />
            </div>

            <!-- Everything for the job search -->
            <div v-if="canBuyPack" class="rounded-[0.625rem] border-2 border-ink p-4 dark:border-white">
              <span class="tag mb-2">{{ t('landing.builder.pack.badge') }}</span>
              <div class="flex items-baseline justify-between gap-3">
                <div class="font-title text-lg font-bold text-ink dark:text-white">{{ t('premium.jobPack.title') }}</div>
                <span class="font-title text-lg font-bold text-ink dark:text-white">{{ packCredit.packPrice.value }}</span>
              </div>
              <ul class="mb-4 mt-2 space-y-1.5">
                <li v-for="line in ['cvs', 'checks', 'writing', 'interview', 'web']" :key="line" class="flex items-start gap-2 !text-sm text-body dark:text-gray-300">
                  <i class="bi bi-check-lg mt-0.5 shrink-0 text-accent" aria-hidden="true" />
                  {{ t(`premium.jobPack.stack.lines.${line}.title`) }}
                </li>
              </ul>
              <SofaxBtn
                variant="accent"
                block
                :disabled="isStartingCheckout || isScriptLoading"
                :label="t('premium.jobPack.cta', { price: packCredit.packPrice.value })"
                @click="buyFromChoice('job-pack')"
              />
              <p class="mt-2 text-center !text-sm text-muted dark:text-gray-400">{{ t('landing.builder.pack.noRenew') }}</p>
            </div>
          </div>

          <!-- The free ways out: the same CV in Basic without a watermark, or this layout with one -->
          <div v-if="freeDownload && choiceFor === 'download'" class="mt-5 space-y-2 text-center">
            <SofaxBtn variant="outline" block :label="t('landing.builder.corner.basicFree')" @click="basicFromChoice" />
            <button type="button" class="link !text-sm" @click="freeFromChoice">{{ t('landing.builder.corner.orFree') }}</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- The free Basic download clicked: their CV for free, or in a designed layout, clean -->
    <Teleport to="body">
      <div
        v-if="showDesignUpsell"
        class="fixed inset-0 z-[1200] flex items-start justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-4"
        @click.self="showDesignUpsell = false"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="design-upsell-title"
          class="sofax max-h-[100dvh] w-full max-w-2xl overflow-y-auto rounded-b-2xl bg-white p-4 shadow-lift sm:max-h-[92svh] sm:rounded-[0.625rem] sm:p-8 dark:bg-gray-800"
          @keydown.esc="showDesignUpsell = false"
        >
          <button
            type="button"
            class="float-right -mr-2 -mt-2 inline-flex size-9 items-center justify-center rounded-full text-ink hover:bg-surface dark:text-white dark:hover:bg-gray-700"
            :aria-label="t('common.close')"
            @click="showDesignUpsell = false"
          >
            <i class="bi bi-x-lg" />
          </button>
          <!-- Phones: the dialog hangs from the top and keeps its text small, so both buttons are in view above the browser's toolbar -->
          <h3 id="design-upsell-title" class="mb-1 !text-xl sm:!text-2xl">{{ t('landing.builder.designUpsell.title') }}</h3>
          <p class="!mb-3 !text-sm sm:!mb-5 sm:!text-base">{{ t('landing.builder.designUpsell.text') }}</p>

          <div class="grid grid-cols-2 gap-3 sm:gap-5">
            <!-- Free, as promised -->
            <div class="flex min-w-0 flex-col">
              <div class="relative aspect-[1/1.414] overflow-hidden rounded-md border border-line bg-gray-100 dark:border-gray-700 dark:bg-gray-900">
                <LazyCVPreviewWrapper
                  :cv-data="cvData"
                  :experiences-list="cvData.experiences || []"
                  :education-list="cvData.education || []"
                  :show-q-r-code="false"
                  :template="FREE_CLEAN_TEMPLATE"
                  frame="border"
                />
              </div>
              <div class="mt-3 flex items-baseline justify-between gap-2">
                <span class="truncate font-title font-bold text-ink dark:text-white">{{ t(`cv.templates.${FREE_CLEAN_TEMPLATE}.name`) }}</span>
                <span class="shrink-0 font-title font-bold text-accent-strong dark:text-accent-soft">{{ t('cvChat.templates.free') }}</span>
              </div>
              <p class="mt-0.5 !text-xs text-muted sm:!text-sm dark:text-gray-400">{{ t('landing.builder.designUpsell.freeNote') }}</p>
            </div>

            <!-- The same CV, designed and clean -->
            <div class="flex min-w-0 flex-col">
              <div class="relative aspect-[1/1.414] overflow-hidden rounded-md border-2 border-ink bg-gray-100 dark:border-white dark:bg-gray-900">
                <LazyCVPreviewWrapper
                  :cv-data="cvData"
                  :experiences-list="cvData.experiences || []"
                  :education-list="cvData.education || []"
                  :show-q-r-code="false"
                  :template="designTemplate"
                  frame="border"
                />
              </div>

              <!-- Another paid layout: the arrows step through them, the small pages pick one -->
              <div class="mt-2 flex items-center gap-1">
                <button type="button" class="design-step" :aria-label="t('cv.templates.previousTemplate')" @click="stepDesign(-1)">
                  <i class="bi bi-chevron-left" aria-hidden="true" />
                </button>
                <div ref="designStripRef" role="radiogroup" :aria-label="t('cv.templates.selectTemplate')" class="design-strip flex min-w-0 grow gap-1.5 overflow-x-auto px-0.5 py-1">
                  <button
                    v-for="id in designTemplates"
                    :key="id"
                    type="button"
                    role="radio"
                    :data-template="id"
                    :aria-checked="designTemplate === id"
                    :aria-label="t(`cv.templates.${id}.name`)"
                    :title="t(`cv.templates.${id}.name`)"
                    class="w-8 shrink-0 overflow-hidden rounded-sm border bg-white transition sm:w-9 dark:bg-gray-800"
                    :class="designTemplate === id ? 'border-ink ring-2 ring-ink dark:border-white dark:ring-white' : 'border-line opacity-70 hover:opacity-100 dark:border-gray-600'"
                    @click="pickDesign(id)"
                  >
                    <NuxtImg :src="`/${id}.png`" alt="" format="webp" quality="70" width="72" height="102" loading="lazy" class="aspect-[1/1.414] w-full object-cover object-top" />
                  </button>
                </div>
                <button type="button" class="design-step" :aria-label="t('cv.templates.nextTemplate')" @click="stepDesign(1)">
                  <i class="bi bi-chevron-right" aria-hidden="true" />
                </button>
              </div>

              <div class="mt-2 flex items-baseline justify-between gap-2">
                <span class="truncate font-title font-bold text-ink dark:text-white">{{ designTemplateName }}</span>
                <span class="shrink-0 font-title font-bold text-ink dark:text-white">{{ exportCreditPrice }}</span>
              </div>
              <p class="mt-0.5 !text-xs text-muted sm:!text-sm dark:text-gray-400">{{ t('landing.builder.designUpsell.paidNote') }}</p>
            </div>
          </div>

          <!-- Each under its card, on phones too: smaller pills with short labels -->
          <div class="design-cta mt-3 grid grid-cols-2 gap-3 sm:mt-4 sm:gap-5">
            <SofaxBtn
              variant="outline"
              block
              :disabled="isExporting || checkingEmail"
              :label="freeDesignLabel"
              @click="designUpsellFree"
            />
            <SofaxBtn
              variant="accent"
              block
              :disabled="isStartingCheckout || isScriptLoading"
              :label="paidDesignLabel"
              @click="designUpsellBuy"
            />
          </div>

          <p class="mt-3 text-center !text-xs text-muted sm:mt-4 sm:!text-sm dark:text-gray-400">{{ t('landing.builder.designUpsell.more') }}</p>
        </div>
      </div>
    </Teleport>

    <!-- Before keeping the CV to edit later: where to send its link -->
    <Teleport to="body">
      <div
        v-if="showSaveGate && generationId"
        class="fixed inset-0 z-[1200] flex items-end justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-4"
        @click.self="showSaveGate = false"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="save-gate-title"
          class="sofax max-h-[92svh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-6 shadow-lift sm:rounded-[0.625rem] sm:p-8 dark:bg-gray-800"
          @keydown.esc="showSaveGate = false"
        >
          <button
            type="button"
            class="float-right -mr-2 -mt-2 inline-flex size-9 items-center justify-center rounded-full text-ink hover:bg-surface dark:text-white dark:hover:bg-gray-700"
            :aria-label="t('common.close')"
            @click="showSaveGate = false"
          >
            <i class="bi bi-x-lg" />
          </button>
          <!-- Sent: the CV is kept by its link; the account is the next step, now or later -->
          <template v-if="saveSentTo">
            <h3 id="save-gate-title" class="mb-3 !text-2xl">{{ t('landing.emailCv.saveSentTitle') }}</h3>
            <p class="!text-sm !text-body dark:!text-gray-300">
              <i class="bi bi-envelope-check mr-1 text-accent" aria-hidden="true" />
              {{ t('landing.emailCv.saveSentText', { email: saveSentTo }) }}
            </p>
            <SofaxBtn class="mt-5" variant="dark" block :label="t('landing.emailCv.saveAccount')" @click="accountFromSaveGate" />
            <p class="mt-4 text-center">
              <button type="button" class="link !text-sm" @click="showSaveGate = false">{{ t('landing.emailCv.saveLater') }}</button>
            </p>
          </template>
          <template v-else>
            <h3 id="save-gate-title" class="mb-3 !text-2xl">{{ t('landing.emailCv.saveTitle') }}</h3>
            <LazyLandingEmailCvCard
              gate
              purpose="save"
              :cv-data="cvData"
              :generation-id="generationId"
              :template="template"
              :consent-source="consentSource"
              @sent="onSaveSent"
              @unsendable="onSaveUnsendable"
            />
            <!-- Or straight into an account: the sign-up asks for the address and the same consent -->
            <div class="my-4 flex items-center gap-3 !text-sm text-muted dark:text-gray-400" aria-hidden="true">
              <span class="h-px grow bg-line dark:bg-gray-700" />
              {{ t('landing.emailCv.saveOr') }}
              <span class="h-px grow bg-line dark:bg-gray-700" />
            </div>
            <SofaxBtn variant="outline" block :label="t('landing.emailCv.saveToAccount')" @click="accountFromSaveGate" />
            <p class="mt-2 text-center !text-xs text-muted dark:text-gray-400">{{ t('landing.emailCv.saveToAccountNote') }}</p>
          </template>
        </div>
      </div>
    </Teleport>

    <!-- Before the free download: where to send the CV -->
    <Teleport to="body">
      <div
        v-if="showEmailGate && generationId"
        class="fixed inset-0 z-[1200] flex items-end justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-4"
        @click.self="showEmailGate = false"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-gate-title"
          class="sofax max-h-[92svh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-6 shadow-lift sm:rounded-[0.625rem] sm:p-8 dark:bg-gray-800"
          @keydown.esc="showEmailGate = false"
        >
          <button
            type="button"
            class="float-right -mr-2 -mt-2 inline-flex size-9 items-center justify-center rounded-full text-ink hover:bg-surface dark:text-white dark:hover:bg-gray-700"
            :aria-label="t('common.close')"
            @click="showEmailGate = false"
          >
            <i class="bi bi-x-lg" />
          </button>
          <!-- Confirmed (back from the inbox): the download, with the click that starts it -->
          <template v-if="emailConfirmed">
            <h3 id="email-gate-title" class="mb-3 !text-2xl">{{ t('landing.emailCv.gateConfirmedTitle') }}</h3>
            <SofaxBtn variant="accent" block :label="t('landing.emailCv.gateDownload')" @click="downloadAfterConfirm" />
          </template>
          <!-- Sent: waiting for the click on the link in the e-mail -->
          <template v-else-if="gateSentTo">
            <h3 id="email-gate-title" class="mb-3 !text-2xl">{{ t('landing.emailCv.gateSentTitle') }}</h3>
            <p class="!text-sm !text-body dark:!text-gray-300">
              <i class="bi bi-envelope-check mr-1 text-accent" aria-hidden="true" />
              {{ t('landing.emailCv.gateSentText', { email: gateSentTo }) }}
            </p>
            <p v-if="gateNotYet" class="mt-3 !text-sm text-amber-700 dark:text-amber-400" role="status">{{ t('landing.emailCv.gateNotYet') }}</p>
            <SofaxBtn
              class="mt-5"
              variant="dark"
              block
              :disabled="checkingEmail"
              :label="t('landing.emailCv.gateCheck')"
              @click="downloadAfterConfirm"
            />
            <p class="mt-4 text-center">
              <button type="button" class="link !text-sm" @click="gateSentTo = ''">{{ t('landing.emailCv.gateOtherEmail') }}</button>
            </p>
          </template>
          <template v-else>
            <h3 id="email-gate-title" class="mb-3 !text-2xl">{{ t('landing.emailCv.gateTitle') }}</h3>
            <LazyLandingEmailCvCard
              gate
              :cv-data="cvData"
              :generation-id="generationId"
              :template="template"
              :consent-source="consentSource"
              @sent="onGateSent"
              @unsendable="onGateUnsendable"
            />
          </template>
        </div>
      </div>
    </Teleport>

    <!-- The checkout (LemonSqueezy, or Stripe in crowns), kept in-page so the visitor never loses the CV -->
    <Teleport to="body">
      <div v-if="checkoutUrl" class="sofax fixed inset-0 z-[1300] flex items-center justify-center bg-black/70 p-2 sm:p-4">
        <div class="relative h-[92vh] w-full max-w-5xl overflow-hidden rounded-[0.625rem] bg-white shadow-lift dark:bg-gray-900">
          <button
            type="button"
            class="social-link absolute right-3 top-3 z-10 shadow-lift"
            :aria-label="t('common.close')"
            @click="checkoutUrl = null"
          >
            <i class="bi bi-x-lg" />
          </button>
          <CommonCheckoutLoading />
          <CommonCheckoutEmbed :src="checkoutUrl" />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.design-step {
  @apply inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-ink text-sm text-white transition-opacity hover:opacity-80 dark:bg-white dark:text-ink;
}

/* Two pills in one row on a phone: without their 12.5rem minimum, and smaller */
@media (max-width: 639px) {
  .design-cta :deep(.btn) {
    @apply min-w-0 px-3 py-3 text-sm;
  }
}

/* C's phone bar: a compact pill next to its line, not the 12.5rem default */
.result-bar :deep(.btn) {
  @apply min-w-0 px-5 py-3 text-sm;
}

/* The small pages scroll sideways on a phone; no scrollbar under them */
.design-strip {
  scrollbar-width: none;
}

.design-strip::-webkit-scrollbar {
  display: none;
}
</style>
